import { z } from 'zod';
/**
 * THE WebSocket envelope + op/channel vocabulary (E17, CODE_GUIDELINES §5).
 *
 * Everything is WebSockets. One JSON object per frame, both directions.
 */
export const wsFrameType = z.enum(['req', 'res', 'push', 'err', 'ack', 'ping', 'pong']);
export const wsEnvelope = z.object({
    type: wsFrameType,
    id: z.string().uuid().optional(), // correlation id: required on req/res/err; absent on push/ping/pong
    channel: z.string().optional(), // required on push & on subscribe/unsubscribe ops
    op: z.string().optional(), // operation name on req, e.g. 'document.create'
    payload: z.unknown().optional(), // validated per-op by the op's own schema
    ts: z.number().int(), // ms epoch, set by sender
});
/**
 * Closed set of dot-namespaced ops (§5.1). An unknown `op` → `err` `BAD_OP`.
 * Each op has a paired request- and response-payload schema elsewhere in this
 * package (see ./auth.dto.ts, ./document.dto.ts, etc.).
 */
export const wsOp = z.enum([
    // auth / session (F12/F13/F42/F43/F44/F54)
    'auth.handshake',
    'auth.resume',
    'auth.login',
    'auth.signup',
    'auth.magicLink.request',
    'auth.magicLink.consume',
    'auth.passwordReset.request',
    'auth.passwordReset.confirm',
    'auth.logout',
    'session.list',
    'session.revoke',
    // channels
    'channel.subscribe',
    'channel.unsubscribe',
    // documents / generation (F1/F2/F3/F4/F15/F17)
    'document.create',
    'document.list',
    'document.get',
    'document.expand',
    'document.cut',
    'document.difficulty',
    'document.translate',
    'outline.confirm',
    'outline.reject',
    // generate flow — F1/F-Estep/F15 wizard (M1)
    'generate.estimate',
    'generate.start',
    // async jobs — F14/E11 job tray + reattach (M1)
    'job.subscribe',
    'job.get',
    'job.logs',
    // sharing / social (F27/F28/F29/F30/F62/F63/F64/F72)
    'share.publish',
    'share.update',
    'share.revoke',
    'share.get',
    'share.list',
    'share.grant',
    'share.ungrant',
    'link.issue',
    'link.revoke',
    'link.resolve',
    'discover.list',
    'discover.get',
    'comment.create',
    'comment.list',
    'comment.remove',
    'reaction.set',
    'suppression.add',
    'suppression.remove',
    'referral.list',
    'referral.stats',
    // moderation (F69/F74)
    'moderation.takedown',
    'queue.list',
    'queue.resolve',
    // killer cost previews (F2/F3/F4/F17) — paired with document.expand/cut/difficulty/translate above
    'expand.estimate',
    'cut.estimate',
    'difficulty.estimate',
    'translate.estimate',
    // editor (F65/F66/F67)
    'version.list',
    'version.get',
    'version.diff',
    'editor.save',
    'reader.position.get',
    'reader.position.set',
    'style.apply',
    'export.start',
    // explorer / folders (F7)
    'file.list',
    'file.get',
    'file.delete',
    'file.rename',
    'file.move',
    'folder.create',
    'folder.rename',
    'folder.move',
    'folder.delete',
    // Google Drive connect/browse/import (F6)
    'drive.connect',
    'drive.status',
    'drive.disconnect',
    'drive.list',
    'drive.import',
    // public-domain corpus browse (F5)
    'corpus.list',
    'corpus.get',
    // search / discovery (F8/F30/F31)
    'search.query',
    'category.list',
    // notifications (F33)
    'notification.read',
    'notification.list',
    'notification.markRead',
    // web push (F22)
    'push.subscribe',
    'push.unsubscribe',
    // settings (F10/F16/E18)
    'settings.setLocale',
    'settings.setTheme',
    'settings.updateNotificationPrefs',
    'settings.getProfile',
    'settings.updateProfile',
    // account lifecycle (F10 Danger Zone — delete/export, self-service only)
    'account.delete',
    'account.exportData',
    // personas (F16)
    'persona.create',
    'persona.update',
    'persona.delete',
    'persona.list',
    // templates (F1 "Start faster" gallery, `/templates`)
    'template.list',
    // activity (F49)
    'activity.list',
    // FTUE tutorial (F9) — read side reuses settings.getProfile's ftueCompletedAt
    'ftue.complete',
    // billing (F19/F48/F56/F57)
    'promo.redeem',
    'credits.estimate',
    'credits.balance',
    'credits.ledger',
    // billing — checkout / subscription / invoices (F20/F24/F26/F45/F46/F57/M4)
    'billing.checkout.start',
    'billing.subscription.get',
    'billing.subscription.cancel',
    'billing.invoices.list',
    // admin / staff (F53–F61, F68, F70, F71)
    'impersonation.start',
    'impersonation.end',
    'admin.users.list',
    'admin.users.get',
    'admin.users.update',
    'admin.users.creditAdjust',
    'admin.users.forceLogout',
    'admin.audit.list',
    'admin.assets.list',
    'admin.assets.unpublish',
    'admin.assets.remove',
    'admin.promo.list',
    'admin.promo.create',
    'admin.promo.update',
    'admin.promo.deactivate',
    'admin.contact.list',
    'admin.contact.handle',
    'admin.analytics.overview',
    'admin.analytics.timeseries',
    'admin.billing.overview',
    'admin.cms.blocksList',
    'admin.cms.blocksSave',
    'admin.cms.blocksPublish',
    // deploy-invalidation (E9, WS-PWA) — admin-only manual re-trigger of the
    // `deploy:broadcast` push; the primary path is the boot-time auto-publish
    // (see `apps/server/src/deploy/WIRING.md`), this op exists for ops/staff
    // to re-announce without a redeploy (e.g. a client that missed the push).
    'deploy.announce',
]);
/**
 * The nine §5.5-documented server-push channel name patterns, plus
 * `server:shutdown` (E16 §14 graceful-shutdown hint — added after the fact,
 * NOT in the §5.5 table). It gets its own channel/schema rather than
 * reusing `deploy:broadcast` precisely so `deployBroadcastPush`'s pinned
 * `{version}` shape (§10.3/E9) never has to carry a second, unrelated
 * payload shape — see `serverShutdownPush` below. Build with these; the
 * server authorizes every subscription against `parseChannel`.
 */
export const wsChannel = {
    user: (userId) => `user:${userId}`,
    job: (jobId) => `job:${jobId}`,
    document: (documentId) => `document:${documentId}`,
    outlineConfirm: (documentId) => `outline-confirm:${documentId}`,
    share: (shareId) => `share:${shareId}`,
    presence: (shareId) => `presence:${shareId}`,
    search: (userId) => `search:${userId}`,
    deployBroadcast: 'deploy:broadcast',
    adminContactInbox: 'admin:contact-inbox',
    serverShutdown: 'server:shutdown',
};
/** Discriminant for a parsed channel. */
export const wsChannelKind = z.enum([
    'user',
    'job',
    'document',
    'outline-confirm',
    'share',
    'presence',
    'search',
    'deploy-broadcast',
    'admin-contact-inbox',
    'server-shutdown',
]);
const PARAMETERIZED = [
    ['user', /^user:(.+)$/],
    ['job', /^job:(.+)$/],
    ['document', /^document:(.+)$/],
    ['outline-confirm', /^outline-confirm:(.+)$/],
    ['share', /^share:(.+)$/],
    ['presence', /^presence:(.+)$/],
    ['search', /^search:(.+)$/],
];
/**
 * Parse a channel string into `{ kind, id }`, or `null` if it matches none of
 * the nine §5.5 patterns plus `server:shutdown` (the server rejects such a
 * subscribe). Used for authorization routing in @xdocs/server /
 * @xdocs/ws-protocol.
 */
export function parseChannel(channel) {
    if (channel === wsChannel.deployBroadcast)
        return { kind: 'deploy-broadcast', id: null };
    if (channel === wsChannel.adminContactInbox)
        return { kind: 'admin-contact-inbox', id: null };
    if (channel === wsChannel.serverShutdown)
        return { kind: 'server-shutdown', id: null };
    for (const [kind, re] of PARAMETERIZED) {
        const m = re.exec(channel);
        if (m && m[1])
            return { kind, id: m[1] };
    }
    return null;
}
// --- handshake / resume payloads (§5.2, §5.7) --------------------------------
/** First frame on socket open (§5.2). */
export const handshakePayload = z.object({
    accessToken: z.string().min(1),
});
/** Reply: identity + gating hints cached on the socket for UI only. */
export const handshakeResult = z.object({
    userId: z.string().uuid(),
    sessionId: z.string().uuid(),
});
/** `auth.resume` — server replays outbox rows newer than this id (§5.7/E20). */
export const resumePayload = z.object({
    lastSeenOutboxId: z.string().nullable(),
});
export const resumeResult = z.object({
    replayed: z.number().int().nonnegative(),
    /** True when the client's `lastSeenOutboxId` predates outbox retention (~24h)
     *  and it must reconcile via a state-diff read instead. */
    gap: z.boolean(),
});
// --- channel sub/unsub payloads ---------------------------------------------
export const channelSubscribePayload = z.object({ channel: z.string().min(1) });
export const channelUnsubscribePayload = channelSubscribePayload;
/** Payload of the `deploy:broadcast` push (E9). */
export const deployBroadcastPush = z.object({ version: z.string().min(1) });
/**
 * Request payload of the admin-only `deploy.announce` op (E9, WS-PWA): no
 * client input needed — the server stamps its own `env.buildVersion` onto
 * the resulting `deploy:broadcast` push, never a client-supplied version
 * (a client can't be trusted to announce truthfully which build is live).
 * The op's response reuses {@link deployBroadcastPush}'s shape (it echoes
 * back the version that was actually broadcast).
 */
export const deployAnnounceInput = z.object({}).strict();
/**
 * Payload of the `server:shutdown` push (E16 §14 graceful shutdown):
 * server → every currently-connected socket, sent right as SIGTERM drain
 * begins, so the client can proactively start migrating to a fresh pod
 * while this connection keeps serving until it drains or the deadline
 * force-closes it (`apps/server/src/shutdown.ts`). Deliberately empty — the
 * channel name alone is the whole signal — never `deploy:broadcast`'s
 * `{version}` shape.
 */
export const serverShutdownPush = z.object({}).strict();
